import React, { memo, useEffect, useState, useRef } from "react";
import { Button } from "antd";
import styled from "styled-components";
import IntlMessages from "../Message/IntlMessages";
import DangerousMessage from "components/Message/DangerousMessage";
import ImgLogoNext from "assets/images/logo-next.svg";
import ImgLogoNextWhite from "assets/images/logo-next-white.svg";
import { ProductAPI } from "services/product";
import BottomSheet from "components/BottomSheet";
import { copyTextToClipBoard } from "utils/common-util";
import WarningIcon from "components/Icon/WarningIcon";

const BgIcon = styled.div`
  --shadow: 0px 6px 20px 2px rgba(255, 87, 87, 0.61);
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--text-error);
  box-shadow: var(--shadow);
  -webkit-box-shadow: var(--shadow);
  -moz-box-shadow: var(--shadow);
  margin: 0 auto;
  margin-top: 20px;
  border-radius: 30rem;

  i {
    color: white;
    font-size: 3rem;
  }
`;

// Constants
const URL_TIMEOUT_MS = 1000 * 60 * 2; // 2 minutes

const ValidateDepositAccount = ({ close, actions, state, ...modalProps }) => {
  const [deeplinkUpgradeUrl, setDeeplinkUpgradeUrl] = useState(null);
  const [deeplinkOpenNewAccountUrl, setDeeplinkOpenNewAccountUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef(null);
  const isWarning = state?.accAlertType === "WARNING";
  const isError =
    state?.accAlertType === "ERROR" || state?.accAlertType === "GS_ERROR" || state?.accAlertType === "THB";

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (deeplinkUpgradeUrl || deeplinkOpenNewAccountUrl) {
      timeoutRef.current = setTimeout(() => {
        setDeeplinkUpgradeUrl(null);
        setDeeplinkOpenNewAccountUrl(null);
        timeoutRef.current = null;
      }, URL_TIMEOUT_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [deeplinkUpgradeUrl, deeplinkOpenNewAccountUrl]);

  // Helper Functions
  const extractErrorMessage = (error) => {
    if (!error) return { message: "เกิดข้อผิดพลาด", details: "" };

    if (typeof error === "string") {
      return { message: error, details: "" };
    }

    if (error?.message) {
      return { message: error.message, details: error?.stack || "" };
    }

    if (error?.response?.data?.message) {
      return { message: error.response.data.message, details: "" };
    }

    if (error?.response?.data?.error) {
      return { message: error.response.data.error, details: "" };
    }

    return { message: "เกิดข้อผิดพลาด", details: "" };
  };

  const copyUrlToClipboard = (url) => {
    try {
      const copied = copyTextToClipBoard(url);
      if (copied) {
        console.log(`[ValidateDepositAccount] URL copied to clipboard: ${url}`);
      } else {
        console.warn(`[ValidateDepositAccount] Failed to copy URL to clipboard: ${url}`);
      }
      return copied;
    } catch (error) {
      console.warn(`[ValidateDepositAccount] Error copying URL to clipboard:`, error);
      return false;
    }
  };

  const navigateToUrl = (url, onSuccess) => {
    if (!url) return false;

    copyUrlToClipboard(url);

    // Replace parent tab - this ensures popups/dialogs appear in the current tab
    // and avoids Safari popup issues with new tabs
    window.location.replace(url);
    onSuccess?.();
    return true;
  };

  const handleError = (error) => {
    console.error(error);
    const { message } = extractErrorMessage(error);
    alert(`เกิดข้อผิดพลาด: ${message}`);
  };

  const handleNoUrlError = () => {
    const errorMessage = "ไม่พบ URL ใน response จาก server";
    console.error(errorMessage);
    alert(errorMessage);
  };

  async function handleAccountAction(apiCall, setDeeplinkUrl, deeplinkUrl) {
    try {
      setIsLoading(true);

      let _deeplinkUrl = deeplinkUrl;

      if (!_deeplinkUrl) {
        const response = await apiCall({
          source: state?.source,
          sfcdCurrencyCodes: state?.sfcdCurrencyCodes,
        });

        // Mock response that simulates a call returning google.com
        // const response = await new Promise((resolve) => {
        //   setTimeout(() => {
        //     resolve({
        //       data: {
        //         result: "https://www.google.com",
        //       },
        //     });
        //   }, 3000); // 3 seconds delay
        // });

        // Promise mock response with error after 3 seconds
        // const response = await new Promise((_, reject) => {
        //   setTimeout(() => {
        //     reject(new Error("Mock error after 3 seconds"));
        //   }, 3000);
        // });

        if (response?.data?.result) {
          _deeplinkUrl = response.data.result;
          setDeeplinkUrl(_deeplinkUrl);
        }
      } else {
        setDeeplinkUrl(null);
      }

      if (_deeplinkUrl) {
        navigateToUrl(_deeplinkUrl, onCancel);
      } else {
        handleNoUrlError();
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function onUpgradeAccount() {
    await handleAccountAction(ProductAPI.upgradeGlobalSavingsAccount, setDeeplinkUpgradeUrl, deeplinkUpgradeUrl);
  }

  async function onOpenNewAccount() {
    await handleAccountAction(
      ProductAPI.openNewGlobalSavingsAccount,
      setDeeplinkOpenNewAccountUrl,
      deeplinkOpenNewAccountUrl
    );
  }

  const handleButtonClick = (deeplinkUrl, setDeeplinkUrl, actionCallback) => {
    if (deeplinkUrl) {
      navigateToUrl(deeplinkUrl, () => {
        setDeeplinkUrl(null);
        onCancel();
      });
    } else {
      actionCallback();
    }
  };

  function onNext() {
    close();
    actions?.onNext && actions.onNext();
  }

  function onCancel() {
    close();
    actions?.onCancel && actions.onCancel();
  }

  function prepareMessage(message) {
    if (!message) return null;

    if (message.includes("[IMG:LOGO]")) {
      const imageHtml = `<img src="${ImgLogoNext}" alt="NEXT logo" style="height: 14px; transform: translateY(-2px); vertical-align: middle; display: inline-block;" />`;
      return message.replace(/\[IMG:LOGO\]/g, imageHtml);
    }

    return null;
  }

  function renderMessageIconType() {
    if (isWarning) {
      return (
        <div className="d-flex justify-content-center mt-4">
          <WarningIcon size={60} className="status-icon-warning" />
        </div>
      );
    }

    if (isError) {
      return (
        <BgIcon>
          <i className="feather-icon icon-x"></i>
        </BgIcon>
      );
    }

    return null;
  }

  function renderButtonSet() {
    if (isWarning) {
      return (
        <div className="d-flex flex-column justify-content-center w-100 gap-3">
          <Button className="btn button-ktb-main" onClick={onNext}>
            <IntlMessages id="global.continue.2" />
          </Button>
          <Button
            className="btn button-ktb-line"
            onClick={() => handleButtonClick(deeplinkUpgradeUrl, setDeeplinkUpgradeUrl, onUpgradeAccount)}
            loading={isLoading}
          >
            <IntlMessages id="account.global-savings.upgrade" />
            <img className="ms-2 mb-1" src={ImgLogoNext} alt="NEXT logo" style={{ height: 24 }} />
          </Button>
        </div>
      );
    }

    if (isError) {
      switch (state?.accAlertType) {
        case "ERROR":
          return (
            <div className="d-flex flex-column justify-content-center w-100 gap-3">
              <Button
                className="btn button-ktb-navy-blue flex-fill"
                onClick={() =>
                  handleButtonClick(deeplinkOpenNewAccountUrl, setDeeplinkOpenNewAccountUrl, onOpenNewAccount)
                }
                loading={isLoading}
              >
                <IntlMessages id="account.global-savings.open-new-account" />
                <img className="ms-2 mb-1" src={ImgLogoNextWhite} alt="NEXT logo" style={{ height: 24 }} />
              </Button>
              <Button className="btn button-ktb-line flex-fill" onClick={onCancel}>
                <IntlMessages id="global.do-later" />
              </Button>
            </div>
          );

        case "GS_ERROR":
          return (
            <div className="d-flex flex-column justify-content-center w-100 gap-3">
              <Button
                className="btn button-ktb-navy-blue"
                onClick={() => handleButtonClick(deeplinkUpgradeUrl, setDeeplinkUpgradeUrl, onUpgradeAccount)}
                loading={isLoading}
              >
                <IntlMessages id="account.global-savings.upgrade" />
                <img className="ms-2 mb-1" src={ImgLogoNextWhite} alt="NEXT logo" style={{ height: 24 }} />
              </Button>
              <Button className="btn button-ktb-line flex-fill" onClick={onCancel}>
                <IntlMessages id="global.do-later" />
              </Button>
            </div>
          );

        case "THB":
          return (
            <div className="d-flex flex-column justify-content-center w-100 gap-3">
              <Button className="btn button-ktb-line flex-fill" onClick={onCancel}>
                <IntlMessages id="global.do-later" />
              </Button>
            </div>
          );

        default:
          return null;
      }
    }

    return null;
  }

  if (!state) return null;

  return (
    <BottomSheet {...modalProps}>
      {modalProps.visible && (
        <div className="mnc-modal-style-1">
          {/* Header */}
          <div className="modal-mnc-head">
            <h5 className="m-0">
              <IntlMessages id="account.global-savings.validate.title" />
            </h5>
            <Button className="btn button-ktb-close" onClick={onCancel}>
              <i className="feather-icon icon-x"></i>
            </Button>
          </div>

          {/* Icon */}
          {renderMessageIconType()}

          {/* Message */}
          <div className="modal-mnc-body">
            <DangerousMessage.Div
              className="custom-message-1"
              html={prepareMessage(state?.accAlertMessage) || state?.accAlertMessage}
            />
          </div>

          {/* Button Set */}
          <div className="modal-mnc-footer border-0 pt-0 pb-5">{renderButtonSet()}</div>
        </div>
      )}
    </BottomSheet>
  );
};

export default memo(ValidateDepositAccount);
